// Shared "confirm, then wipe this device's identity/session and land on
// onboarding" flow. Two places trigger it: the explicit Log Out button
// (AccountSettingsScreen's LogOutSection) and terminating the CURRENT
// session from the Sessions screen (SessionsSettingsScreen) — both end this
// device's session locally, so both show the identical recovery warning
// gated behind the identical confirm step before doing it (restoring
// afterwards needs the passkey, recovery phrase, or account password — the
// warning exists so that isn't a surprise). Extracted here rather than
// duplicated in both screens.

import { useCallback, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../../shell/accountContext.js';
import { useT } from '../../i18n/index.js';
import { quietButtonClass } from './chrome.js';

export interface SignOutFlow {
  confirming: boolean;
  busy: boolean;
  requestConfirm: () => void;
  cancel: () => void;
  confirm: () => Promise<void>;
}

/**
 * `beforeWipe` (e.g. revoking this session server-side via `DELETE
 * /sessions/:id`) runs first but is best-effort: once the user has
 * confirmed, the local wipe must happen even if that request never reaches
 * the server — a deliberate local action never blocks on the network (prime
 * directive, docs/00).
 */
export function useSignOutFlow(beforeWipe?: () => Promise<void>): SignOutFlow {
  const { signOut } = useAccount();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const requestConfirm = useCallback(() => setConfirming(true), []);
  const cancel = useCallback(() => setConfirming(false), []);

  const confirm = useCallback(async () => {
    setBusy(true);
    try {
      if (beforeWipe) await beforeWipe().catch(() => undefined);
      await signOut();
      void navigate('/onboarding', { replace: true });
    } finally {
      setBusy(false);
    }
  }, [beforeWipe, signOut, navigate]);

  return { confirming, busy, requestConfirm, cancel, confirm };
}

/**
 * The recovery warning + destructive confirm/cancel step, shown once
 * `flow.requestConfirm()` has fired. Same copy everywhere it appears: the
 * warning is about losing the identity stored on THIS device, which is true
 * regardless of which screen triggered the wipe.
 */
export function SignOutWarning({ flow }: { flow: SignOutFlow }): ReactNode {
  const t = useT();
  return (
    <div className="flex flex-col gap-2">
      <p role="alert" className="text-sm text-ink">
        {t('account.signOutConfirm')}
      </p>
      <button
        type="button"
        disabled={flow.busy}
        onClick={() => void flow.confirm()}
        className="min-h-touch rounded-xl bg-danger px-4 text-sm font-medium text-ink disabled:opacity-40"
      >
        {flow.busy ? t('account.signingOut') : t('account.signOutConfirmButton')}
      </button>
      <button type="button" onClick={flow.cancel} className={quietButtonClass}>
        {t('common.cancel')}
      </button>
    </div>
  );
}
